<template>
  <validation-observer v-slot="{ invalid }" slim>
    <div 
      @close="handleClose" 
      @confirm="handleCloseConfirmation" 
      class="checkout-custom-modal"
      :class="centralizedModals.includes(stepComponent) ? 'modal-centralized' : ''" 
      :showCloseButton="false"
    >
    
      <div slot="header" class="container">
        <header class="header-quote flex justify-between items-center px-4">
          <h4 class="flow-header-title">
            <slot name="title ">{{ headerText }}</slot>
          </h4>
          <a class="close-icon flex items-center justify-center" @click.prevent="handleClose">
            <icon type="x-close" />
          </a>
        </header>
        <step-tabs v-if="quoteSteps.includes(stepComponent)" :current="stepComponent" :steps="steps" />
      </div>

      <div v-if="prompt" slot="prompt" class="w-full h-[75%] flex flex-col items-center justify-center font-rubik">
        <h5 class="font-semibold text-lg 1170-bp:text-2xl">Are you sure you'd like to leave?</h5>
        <div class="flex gap-8 mt-8">
          <button @click="handleCloseConfirmation" class="w-[111px] 1170-bp:w-[222px] h-[44px] rounded-full border border-red-500 bg-red-500 transition-all">
            <p class="white text-white">Yes</p>
          </button>
          <button @click="prompt = !prompt" class="w-[111px] 1170-bp:w-[222px] h-[44px] rounded-full border border-[#4D84FF] bg-[#4D84FF] transition-all">
            <p class="white text-white">No</p>
          </button>
        </div>
      </div>

      <div v-if="!prompt" class="flex flex-col justify-between" style="height: calc(100% - 185px) !important;">
        
        <div :class="stepComponent != 'options' ? 'component-slot' : ''" slot="body">
          <component :is="stepComponent" ref="stepComponent" @next="next" @close="handleClose"
            @enabled="(enabled) => disabled = !enabled">
          </component>
        </div>

        <div  slot="footer" class="flex flex-col">
          <div class="summary-totals" v-if="quote && ['options', 'checkout', 'confirmation'].includes(stepComponent)">
            <!--<quote-totals />-->
          </div>

          <div class="buttons-container">
            <div v-if="customSize">
              <button @click="backToHome" class="button custom-action-button is-fullwidth transition-all">
                <p>Back to Home</p>
              </button>
            </div>
            <div v-else>
              <div class="level is-mobile">
                <template v-if="isComplete">
                  <div class="level-item">
                    <a href="/solutions/container-sales" class="button custom-close-button is-fullwidth transition-all">
                      <p>See Other Containers</p>
                    </a>
                    <button @click="handleClose" class="button custom-action-button is-fullwidth transition-all">
                      <p>Close</p>
                    </button>
                  </div>
                </template>
                <template v-else>
                  <div v-if="canGoBack" class="level-item is-halfwidth">
                    <button @click="back()" class="button custom-close-button is-fullwidth transition-all">
                      <p>Back</p>
                    </button>
                  </div>
                  <div class="level-item is-halfwidth">
                    <button @click="next" :disabled="invalid || disabled || loading"
                      :class="[
                        'is-fullwidth custom-action-button transition-all flex justify-center items-center',
                        {'is-loading': loading},
                        {'cursor-not-allowed' : (invalid || disabled || loading)}
                      ]"
                    >
                      <p>{{ nextButtonText }}</p>
                    </button>
                  </div>
                </template>
              </div>
              <div v-if="(stepComponent == 'options' || stepComponent == 'email-quote') && showPrice" class="is-fullwidth">
                <p class="neutral-gray has-text-weight-semibold text-sm mb-2 has-text-centered">
                  Ready to make a purchase?
                </p>
                <button @click="reserveOrder" class="button custom-action-button is-fullwidth transition-all"
                  :disabled="invalid || disabled || loading" :class="{ 'is-loading': loading }">
                  <p>Reserve Order</p>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </validation-observer>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import hasModal from 'shared/mixins/has-modal'
import { ValidationObserver } from 'vee-validate'
import Start from './steps/start.vue'
import PurchaseType from './steps/purchase-type.vue'
import UseCase from './steps/use-case.vue'
import Size from './steps/size.vue'
import Condition from './steps/condition.vue'
import ConditionCost from './steps/condition-cost.vue'
import EmailQuote from './steps/email-quote.vue'
import Customer from './steps/customer.vue'
import Thanks from './steps/thanks.vue'
import Checkout from './steps/checkout.vue'
import Confirmation from './steps/confirmation.vue'
import Quote from './steps/quote.vue'

import QuoteTotals from '../summary/totals.vue'
import StepTabs from './steps/helpers/step-tabs.vue'
import Icon from './steps/helpers/icon.vue'

export default {
  mixins: [hasModal],
  props: {
    completeQuoteModal: {
      type: Boolean,
      default: false
    },
    gtag: String
  },
  components: {
    ValidationObserver,
    Start,
    PurchaseType,
    UseCase,
    Size,
    Condition,
    ConditionCost,
    Customer,
    EmailQuote,
    Thanks,
    Checkout,
    Confirmation,
    QuoteTotals,
    Quote,
    StepTabs,
    Icon
  },
  data() {
    return {
      prompt: false,
      loading: false,
      disabled: false,
      centralizedModals: [
        'purchaseType', 'useCase', 'condition', 'quote', 'thanks'
      ]
    }
  },
  computed: {
    ...mapState('quote', ['priceBook', 'lead', 'quote', 'gtagEvent', 'errors']),
    ...mapState('quote/flow', ['priceBookEntries', 'options']),
    ...mapGetters('quote/flow', ['steps', 'stepComponent', 'canGoBack', 'isComplete']),
    nextButtonText() {
      switch (this.stepComponent) {
      case 'checkout':
        return 'Confirm Payment'
      case 'options':
        return this.showPrice ? 'Email Quote' : 'Request Quote'
      case 'email-quote':
        return 'Get Quote'
      case 'start':
        return 'Begin Quote'
      case 'condition':
        return 'See Results'
      case 'quote':
        return 'Close'
      default:
        return 'Next'
      }
    },
    quoteSteps() {
      return ['start', 'useCase', 'size', 'purchaseType', 'condition', 'options']
    },
    headerText() {
      if (this.quoteSteps.includes(this.stepComponent)) {
        return 'Get a Quote'
      } else if (this.stepComponent == 'checkout') {
        return 'Reserve Order'
      } else if (this.stepComponent == 'quote') {
        return 'Quote Submitted'
      }
      return ''
    },
    showPrice() {
      if (this.priceBook.priceVisibility == 'private') {
        return false
      } else if (this.quote) {
        return this.quote.items.filter(item => item.priceBookEntry.priceVisibility == 'private').length == 0
      }
      return true
    },
    customSize () {
      return this.lead?.data?.size === 'CUSTOM'
    }
  },
  async created() {
    await this['pricing.get']()
    if (!this.stepComponent) {
      this.$store.commit('quote/flow/start')
    }
    //window.scrollTo(0, 0)
  },
  methods: {
    ...mapActions('quote', ['pricing.get']),
    setStep(name) {
      this.$store.commit('quote/flow/step.set', this.steps.indexOf(name))
    },
    handleClose() {
      if (this.isComplete) {
        this.$store.commit('quote/reset')
        this.$store.commit('quote/flow/reset')
        this.close()
        this.prompt = false
      } else if (this.prompt) {
        this.$store.commit('quote/flow/close')
        this.close()
        this.prompt = false
      } else {
        this.prompt = true
      }
    },
    handleCloseConfirmation(confirm) {
      if (confirm) {
        return this.close()
      }
      this.prompt = false
    },
    async reserveOrder() {
      if (this.loading) return
      this.loading = true
      const result = await this.$refs.stepComponent.save()
      if (result && result.errors && result.errors.length) {
        // TODO
        console.warn(result.errors)
      } else {
        this.setStep('checkout')
      }
      //window.scrollTo(0, 0)
      this.loading = false
    },
    async saveStepComponent() {
      const result = await this.$refs.stepComponent.save()
      if (result && result.errors && result.errors.length) {
        console.warn(result.errors)
        return false
      }
      return result
    },
    async next() {
      if (!this.lead) {
        this.$store.commit('quote/lead.init')
      }

      if (this.gtag) {
        this.$store.commit('quote/flow/gtag.set', this.gtag)
      }
      if (this.loading) return
      this.loading = true

      const result = await this.saveStepComponent()

      if (this.lead?.data?.size === 'CUSTOM') {
        // Creates a Lead when user selects a custom container size
        try {
          const { lead, errors } = await this.$store.dispatch('quote/lead.save', this.lead)
          if (errors && errors.length) {
            console.warn(errors)
            this.$emit('error', errors)
          } else if (lead) {
            console.log('Lead saved successfully: ', lead.id)
          }
        } catch (e) {
          console.warn(e)
          this.$emit('error', e)
        }
      }

      if (result) {
        if ((this.stepComponent == 'size' && !this.steps.includes('condition')) || this.stepComponent == 'condition' || (this.stepComponent == 'start' && result.hasQuote)) {
          this.redirectToCartPage()
        } else {
          this.$store.commit('quote/flow/next')
        }
      }
      this.loading = false
    },
    redirectToCartPage() {
      window.location.href = '/cart'
    },
    async back() {
      if (this.completeQuoteModal && this.stepComponent == 'email-quote') {
        this.close()
      } else if (this.stepComponent == 'options') {
        await this.$refs.stepComponent.save()
        await this.$store.dispatch('quote/flow/options.compute')
        if (this.lead.data.purchaseType == 'rental') {
          this.setStep('size')
        } else {
          this.setStep('condition')
        }
      } else {
        this.$store.commit('quote/flow/back')
      }
    },
    backToHome() {
      this.$store.commit('quote/reset')
      this.$store.commit('quote/flow/reset')
      window.location.href = '/'
    }
  }
}
</script>

<style lang="scss" scoped>
/* Component Nesting Hax */
.level-item .button-wrapper {
  display: flex;
  width: 100%;

  .js-button {
    display: flex;
    width: 100%;
  }
}

.level-item {
  flex-grow: 0;
}

.is-halfwidth {
  width: 49%;
}

/* Side Drawer */
.checkout-custom-modal {
  position: fixed !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  height: 100vh !important;
  width: 560px !important;
  max-width: 100% !important;
  margin: 0 !important;
  background: #f9f8f6;
  overflow-y: auto !important;
  z-index: 9999 !important;
  border-radius: 0 !important;
  box-shadow: -2px 0 15px rgba(0, 0, 0, 0.2);
}

.modal.is-active {
  display: flex !important;
  align-items: flex-start !important;
  justify-content: flex-end !important;
}


.modal-background {
  position: fixed !important;
  top: 0;
  left: 0;
  width: calc(100% - 400px); 
  height: 100vh;
  background-color: rgba(10, 10, 10, 0.5); 
}

@media (max-width: 768px) {
  .checkout-custom-modal {
    width: 90% !important;   
    right: 0 !important;     
    left: auto !important;  
  }
  .modal.is-active {
    justify-content: flex-end !important; 
  }
  .modal-background {
    width: 10%;               
    left: 0;
    right: auto !important;  
  }
}

.modal-card {
  height: 100%;
  overflow-y: auto;
}

.custom-action-button {
  width: 100%;
  height: 44px;
  background: #4D84FF;
  border-radius: 999px;
  border: 1px solid #4D84FF;
}
.custom-action-button:hover {
  background: #1e44f1 !important;
  border: 1px solid #1e44f1 !important;
}
.custom-action-button p {
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.0125em;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
}

.custom-close-button {
  width: 100%;
  height: 44px;
  background: #FFFFFF;
  border-radius: 999px;
  border: 1px solid #EDE9E3;
}
.custom-close-button:hover {
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
}
.custom-close-button p {
  color: #334155;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.0125em;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
}
</style>

<style lang="scss">
@import './styles/build-quote-modal.scss';

.modal-centralized {
  justify-content: center !important;

  @media (max-width: 520px) {
    justify-content: unset !important;
  }
}

.component-slot {
  padding: 0 20px;
}

/* mobile */
@media(max-width: 1024px) {
  .component-slot {
    padding: 0px 15px;
  }
}

.checkout-custom-modal {
  z-index: 999;
  top: 0 !important;
  bottom: 0 !important;
}

.checkout-custom-modal .neutral-gray {
  color: #767676 !important;
  background-color: #f9f8f6 !important;
}

.checkout-custom-modal .modal-card-foot {
  background-color: white;
  padding: 0;
  border-top: none;
}

.checkout-custom-modal .modal-card-foot .spacing {
  padding-left: 0;
  padding-right: 0;
}

.checkout-custom-modal .action-button {
  background-color: #194985;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.375rem;
}

.checkout-custom-modal .modal-card-foot {
  background-color: #f9f8f6;
  padding: 1.5rem;
}

.buttons-container {
  margin: 24px 0px;
  padding: 0px 15px;
  bottom: 0;
}

.checkout-custom-modal .summary-totals {
  padding: 0 1.5rem 0.5rem 1.5rem;
}

.checkout-custom-modal button[disabled].action-button {
  opacity: 0.5;
  color: #ffffff;
  background-color: #4D84FF;
}

.checkout-custom-modal button:hover.action-button {
  color: #ffffff;
}

.modal-card {
  max-width: 95%;
}

.modal-card-body {
  flex-grow: unset !important;
  max-height: 100% !important;
}

.checkout-custom-modal .modal-card {
  bottom: auto !important;
  top: auto !important;
}

.checkout-custom-modal .modal-card-body {
  padding: 20px 0px;

  .header {
    margin: -20px -20px 20px -20px;
    padding: 1.5em;
    background: #EDEFF6;

  }

  .button.is-primary {
    flex: 1;
  }

  .fieldset {
    padding: 0 0.125rem;

    .field:not(:last-child) {
      margin-bottom: 1.5rem;
    }

    .field.column {
      margin-bottom: 0;
    }

    @media (max-width: 767px) {
      padding: 0 1em;
    }
  }
}

.checkout-custom-modal .image img {
  background: #F3F3F3;
  border-radius: 4px;
}

.radio-list {
  margin: 1em 0 2em 0;

  &:last-child {
    margin-bottom: 0;
  }

  .title {
    margin: 1em 0;
  }

  .radio {
    display: block;

    &+.radio {
      margin-left: 0;
      margin-top: .5em;
    }
  }
}

.flow-header-title {
  color: #334155 !important;
  font-size: 32px;
  font-weight: 400;
  line-height: 41.6px;
  letter-spacing: -0.04em;
}
</style>
